import { graphql, StaticQuery } from 'gatsby';
import Img from 'gatsby-image';
import React, { ChangeEvent } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import sanitizeHtml from 'sanitize-html';
import styled from 'styled-components';

import { ChildImageSharp, LocationProps } from '../../interfaces';
import { AllMarkdownRemarkQueryResult } from '../../interfaces/AllMarkdownRemark';
import { MarkdownFileNode } from '../../interfaces/MarkdownFileNode';
import { Description } from '../../styles/CommonStyledComponents';
import { findMarkdownFile } from '../../utils/FindMarkdownFile';
import { getKindergartenByPathname } from '../../utils/GetKindergartenByPathname';
import { theme } from '../../utils/Theme';
import withLocation from '../../utils/WithLocation';
import { Button } from '../Button';
import Headline from '../headline/Headline';

const Container = styled.div`
  text-align: center;
  position: relative;
`;

const FormContainer = styled.div`
  background-color: ${theme.colors.creamy};
  width: 780px;
  margin: 50px auto;
  padding: 45px 45px 15px 45px;

  input {
    border: 1px solid ${theme.colors.brown};
  }
  textarea {
    width: 100%;
    height: 165px;
    border: 1px solid ${theme.colors.brown};
  }
  ${theme.mediaQuerySizes.tablet} {
    width: auto;
    padding: 45px 30px 15px 30px;
  }
`;

const FormGroup = styled.div`
  padding-bottom: 7px;
  width: 48.5%;
  display: inline-block;
  position: relative;

  ${theme.mediaQuerySizes.phone} {
    width: 100%;
  }
`;

const FormGroupTextarea = styled(FormGroup)`
  width: 100%;
  min-height: 175px;
  margin-bottom: 20px;
`;

const FormGroupLeft = styled(FormGroup)`
  margin-right: 3%;

  ${theme.mediaQuerySizes.phone} {
    margin-right: 0;
  }
`;

const Form = styled.form<FormProps>`
  input,
  textarea {
    width: 100%;
    font-size: 1rem;
    color: #622a14;
    padding: 15px 20px;
    background-color: #fdfbfa;
    font-family: inherit;
    margin-bottom: 2px;
    display: block;

    :focus {
      outline: none !important;
      box-shadow: 0 1rem 2rem rgba(${theme.colors.black}, 0.1);
      border-bottom: 3px solid ${theme.colors.green};
      margin-bottom: 0;
    }
    :focus:invalid {
      border-bottom: 3px solid ${theme.colors.red};
    }

    :placeholder-shown + .form__label {
      opacity: 0;
      visibility: hidden;
      transform: translateY(2.5rem);
    }
  }

  input[type='checkbox'] {
    vertical-align: middle;
    display: inline-block;
    width: 20px;
  }

  label {
    color: #976363;
    font-size: 0.6rem;
    font-weight: 700;
    margin-left: 1.2rem;
    margin-top: 0px;
    display: block;
    text-align: left;
    transition: all 0.3s;
    top: -25px;
  }
`;

const Honeypot = styled.div`
  display: none;
`;

interface FormProps {
  name: string;
  method: string;
  'netlify-honeypot'?: string;
  'data-netlify': string;
}

interface Data {
  'form-name': string;
  'full-name': string;
  phone: string;
  email: string;
  title: string;
  description: string;
}

interface ContactFormProps {
  location: LocationProps;
  data: QueryResponseData;
}

const encode = (data: Data) => {
  let keys = Object.keys(data) as (keyof Data)[];
  return keys.map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key])).join('&');
};

const BackgroundImage = styled.div`
  z-index: -1;
  position: absolute;
  padding: 0;
  top: 63%;
  width: 37%;
  left: -4%;

  ${theme.mediaQuerySizes.tablet} {
    img {
      display: none;
    }
  }
`;

interface QueryResponseData extends AllMarkdownRemarkQueryResult<ContactFormNode> {
  arrow: ChildImageSharp;
  background: ChildImageSharp;
}

interface ContactFormNode extends MarkdownFileNode {
  frontmatter: {
    title: string;
  };
}

interface ContactFormProps {
  location: LocationProps;
  data: QueryResponseData;
}

const query = graphql`
  query {
    arrow: file(relativePath: { eq: "icons/section-arrow-green.png" }) {
      childImageSharp {
        fluid(maxWidth: 200, quality: 75) {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    background: file(relativePath: { eq: "nutka-background.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 800, quality: 75) {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    allMarkdownRemark {
      nodes {
        frontmatter {
          title
        }
        html
        parent {
          ... on File {
            name
            relativeDirectory
          }
        }
      }
    }
  }
`;

class ContactForm extends React.Component<ContactFormProps> {
  data: QueryResponseData;
  location: LocationProps;
  constructor(props: ContactFormProps) {
    super(props);
    this.state = {};
    this.location = props.location;
    this.data = props.data;
  }

  handleChange = (event: ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLInputElement>) => {
    this.setState({ [event.target.name]: event.target.value });
  };

  handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const form = event.target as HTMLInputElement;
    const response = await fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode({
        'form-name': form.getAttribute('name'),
        ...this.state,
      } as Data),
    });
    if (response.status == 200) {
      toast.success('Wiadomość została wysłana', {
        position: 'top-right',
      });
    } else {
      toast.error('Wystąpił błąd podczas wysłania wiadomości', {
        position: 'top-right',
      });
    }
  };

  public render() {
    const markdownFile = findMarkdownFile(this.data.allMarkdownRemark, this.props.location.pathname, 'contact-form');
    return (
      <Container>
        <Headline arrowColor="arrowGreen" title={markdownFile?.frontmatter.title || ''}></Headline>
        <Description
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(markdownFile?.html || ''),
          }}
        ></Description>
        <FormContainer>
          <Form
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            onSubmit={(event) => this.handleSubmit(event)}
            name={getKindergartenByPathname(this.props.location.pathname) + '-contact-form'}
          >
            <Honeypot>
              <label>
                Don’t fill this out if you are human: <input name="bot-field" onChange={this.handleChange} />
              </label>
            </Honeypot>
            <input
              type="hidden"
              name="form-name"
              value={getKindergartenByPathname(this.props.location.pathname) + '-contact-form'}
            />
            <FormGroupLeft>
              <label>
                Imię i nazwisko
                <input type="text" placeholder="Imię i nazwisko" name="Imię i nazwisko" onChange={this.handleChange} />
              </label>
            </FormGroupLeft>
            <FormGroup>
              <label>
                Numer telefonu
                <input type="text" placeholder="Numer telefonu" name="Numer telefonu" onChange={this.handleChange} />
              </label>
            </FormGroup>
            <FormGroupLeft>
              <label>
                Adres email
                <input
                  type="email"
                  placeholder="Adres email*"
                  name="Adres email"
                  onChange={this.handleChange}
                  required
                />
              </label>
            </FormGroupLeft>
            <FormGroup>
              <label>
                Temat wiadomości
                <input
                  type="text"
                  placeholder="Temat wiadomości*"
                  name="Temat wiadomości"
                  onChange={this.handleChange}
                  required
                />
              </label>
            </FormGroup>
            <FormGroupTextarea>
              <label>
                Treść
                <textarea name="Treść" placeholder="Treść" onChange={this.handleChange} />
              </label>
            </FormGroupTextarea>
            <label>
              <input type="checkbox" name="RODO" value="Wyrażono zgodę" onChange={this.handleChange} required />
              Wysyłając formularz wyrażam zgodę na przetwarzanie danych osobowych w celu otrzymania odpowiedzi na tą
              wiadomość. Administratorem Twoich danych jest Niepubliczne Przedszkole Nutka, Słoneczna 30, 05-500 Stara
              Iwiczna, Polska. W każdej chwili masz prawo do usunięcia danych i wypisu.
            </label>
            <Button type="submit" primaryColor={theme.colors.darkGreen} secondaryColor={theme.colors.creamy}>
              Wyślij
            </Button>
          </Form>
        </FormContainer>
        <BackgroundImage>
          <Img alt="background image" fluid={this.data.background.childImageSharp.fluid} />
        </BackgroundImage>
        <ToastContainer />
      </Container>
    );
  }
}

export default withLocation((props: ContactFormProps) => (
  <StaticQuery query={query} render={(data) => <ContactForm {...props} data={data} />} />
));
